// =====================================================================
// Mot de passe oublié, et sa réinitialisation
// =====================================================================
// Deux écrans que l'utilisateur atteint sans être connecté : le premier
// par un lien depuis la connexion, le second par le lien reçu par
// courriel, qui porte le jeton dans l'adresse.
// =====================================================================

import { useState } from 'react';
import { api, ErreurApi } from '../api';
import { Alerte, Champ, IconeCadenas, IconeEnveloppe } from '../composants/base';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Demande du lien de réinitialisation. */
export function MotDePasseOublie({ onRetour }: { onRetour: () => void }) {
  const [email, setEmail] = useState('');
  const [envoye, setEnvoye] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  async function soumettre(e: React.FormEvent) {
    e.preventDefault();
    setErreur(null);
    setEnvoi(true);
    try {
      const r = await api.motDePasseOublie(email.trim());
      setEnvoye(r.message);
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Une erreur est survenue.');
    } finally {
      setEnvoi(false);
    }
  }

  if (envoye) {
    return (
      <div className="carte">
        <h2>Vérifiez vos messages</h2>
        <p className="doux">{envoye}</p>
        <p className="doux">
          Le lien est valable deux heures et ne fonctionne qu'une fois. Pensez
          à regarder dans les courriers indésirables.
        </p>
        <button className="bouton secondaire" onClick={onRetour}>
          Revenir à la connexion
        </button>
      </div>
    );
  }

  return (
    <form className="carte" onSubmit={soumettre} noValidate>
      <h2>Mot de passe oublié</h2>
      <p className="doux" style={{ marginBottom: 16 }}>
        Indiquez l'adresse de votre compte. Nous vous enverrons un lien pour
        en choisir un nouveau.
      </p>

      {erreur && <Alerte>{erreur}</Alerte>}

      <Champ id="oubli-email" etiquette="Adresse e-mail" icone={<IconeEnveloppe />}>
        <input
          id="oubli-email"
          type="email"
          inputMode="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="vous@exemple.com"
          autoComplete="email"
          autoCapitalize="none"
          spellCheck={false}
          required
        />
      </Champ>

      <button
        className="bouton"
        type="submit"
        disabled={!EMAIL.test(email.trim()) || envoi}
      >
        {envoi ? 'Envoi…' : 'Envoyer le lien'}
      </button>

      <div className="separateur" role="presentation">
        <span>ou</span>
      </div>

      <button type="button" className="bouton secondaire" onClick={onRetour}>
        Revenir à la connexion
      </button>
    </form>
  );
}

/** Choix du nouveau mot de passe, depuis le lien reçu. */
export function NouveauMotDePasse({
  jeton,
  onFait,
}: {
  jeton: string;
  onFait: () => void;
}) {
  const [motDePasse, setMotDePasse] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  const concordent = motDePasse.length >= 8 && motDePasse === confirmation;

  async function soumettre(e: React.FormEvent) {
    e.preventDefault();
    setErreur(null);
    setEnvoi(true);
    try {
      await api.reinitialiser(jeton, motDePasse);
      onFait();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Une erreur est survenue.');
    } finally {
      setEnvoi(false);
    }
  }

  return (
    <form className="carte" onSubmit={soumettre} noValidate>
      <h2>Choisir un nouveau mot de passe</h2>
      <p className="doux" style={{ marginBottom: 16 }}>
        Huit caractères au minimum.
      </p>

      {erreur && <Alerte>{erreur}</Alerte>}

      <Champ id="nmdp" etiquette="Nouveau mot de passe" icone={<IconeCadenas />}>
        <input
          id="nmdp"
          type="password"
          value={motDePasse}
          onChange={(e) => setMotDePasse(e.target.value)}
          autoComplete="new-password"
          minLength={8}
          required
        />
      </Champ>

      <Champ id="nmdp2" etiquette="Répétez-le" icone={<IconeCadenas />}>
        <input
          id="nmdp2"
          type="password"
          value={confirmation}
          onChange={(e) => setConfirmation(e.target.value)}
          autoComplete="new-password"
          required
        />
      </Champ>

      {confirmation.length > 0 && !concordent && motDePasse.length >= 8 && (
        <p className="doux" style={{ marginTop: -6, marginBottom: 13 }}>
          Les deux saisies ne correspondent pas.
        </p>
      )}

      <button className="bouton" type="submit" disabled={!concordent || envoi}>
        {envoi ? 'Enregistrement…' : 'Enregistrer'}
      </button>
    </form>
  );
}
